import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Hourglass, QrCode, ShieldCheck, UserCheck } from "lucide-react";
import BookingQRDialog from "@/components/BookingQRDialog";

export default function SelfCheckinPage() {
  const [pending, setPending] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [guestsById, setGuestsById] = useState({});
  const [roomsById, setRoomsById] = useState({});
  const [qrFor, setQrFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pend }, { data: bks }, { data: gs }, { data: rs }] = await Promise.all([
        api.get("/bookings/self-checkin/pending"),
        api.get("/bookings"),
        api.get("/guests"),
        api.get("/rooms"),
      ]);
      setPending(pend);
      setGuestsById(Object.fromEntries(gs.map(g => [g.id, g])));
      setRoomsById(Object.fromEntries(rs.map(r => [r.id, r])));

      // Bookings whose check-in is between today and +14d, not yet completed.
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const horizon = new Date(today.getTime() + 14 * 86400000);
      const up = bks
        .filter(b => {
          if (!["confirmed", "checked_in"].includes(b.status)) return false;
          const ci = new Date(b.check_in);
          return ci >= today && ci <= horizon && b.self_checkin_status !== "completed" && b.self_checkin_status !== "verified";
        })
        .sort((a, b) => a.check_in.localeCompare(b.check_in));
      setUpcoming(up);
    } catch (err) {
      if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (id, alsoCheckIn) => {
    try {
      await api.post(`/bookings/${id}/verify-self-checkin?also_check_in=${alsoCheckIn ? "true" : "false"}`);
      toast.success(alsoCheckIn ? "Identità verificata e check-in effettuato" : "Identità verificata");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">SELF CHECK-IN ONLINE</p>
      <h1 className="font-serif text-4xl tracking-tight mb-2">Check-in dal telefono dell'ospite</h1>
      <p className="text-stone-600 mb-10 max-w-prose">
        Genera un QR per ogni prenotazione: l'ospite scansiona il documento e firma la privacy dal proprio telefono prima di arrivare. Tu confermi l'identità all'arrivo.
      </p>

      {/* ----- Pending verification ----- */}
      <section className="mb-12">
        <header className="flex items-center gap-2 mb-4">
          <UserCheck className="w-5 h-5 text-blue-700" />
          <h2 className="font-serif text-2xl">Da verificare ({pending.length})</h2>
        </header>
        {loading ? (
          <p className="text-stone-500 text-sm">…</p>
        ) : pending.length === 0 ? (
          <p className="text-stone-500 text-sm border border-dashed border-stone-300 p-6 bg-white" data-testid="pending-empty">
            Nessun ospite ha completato il self check-in al momento.
          </p>
        ) : (
          <div className="border border-stone-200 bg-white divide-y divide-stone-200" data-testid="pending-list">
            {pending.map(b => {
              const g = guestsById[b.guest_id];
              const r = roomsById[b.room_id];
              return (
                <div key={b.id} className="p-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-medium" data-testid={`pending-name-${b.id}`}>
                      {g ? `${g.first_name} ${g.last_name}` : "—"}
                    </div>
                    <div className="text-xs text-stone-500">
                      {r?.name || "—"} · {b.check_in} → {b.check_out} · {g?.document_type} {g?.document_number}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200">
                    <Hourglass className="w-3 h-3" /> in attesa
                  </span>
                  <button onClick={() => verify(b.id, false)} data-testid={`verify-btn-${b.id}`}
                    className="inline-flex items-center gap-2 border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-50">
                    <ShieldCheck className="w-4 h-4" /> Verifica
                  </button>
                  <button onClick={() => verify(b.id, true)} data-testid={`verify-checkin-btn-${b.id}`}
                    className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-3 py-1.5 text-sm rounded-sm hover:bg-stone-800">
                    <CheckCircle2 className="w-4 h-4" /> Verifica + Check-in
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ----- Upcoming bookings to send QR for ----- */}
      <section>
        <header className="flex items-center gap-2 mb-4">
          <QrCode className="w-5 h-5 text-blue-700" />
          <h2 className="font-serif text-2xl">In arrivo (prossimi 14 giorni)</h2>
        </header>
        {loading ? (
          <p className="text-stone-500 text-sm">…</p>
        ) : upcoming.length === 0 ? (
          <p className="text-stone-500 text-sm border border-dashed border-stone-300 p-6 bg-white" data-testid="upcoming-empty">
            Nessuna prenotazione in arrivo nei prossimi 14 giorni.
          </p>
        ) : (
          <div className="border border-stone-200 bg-white divide-y divide-stone-200" data-testid="upcoming-list">
            {upcoming.map(b => {
              const g = guestsById[b.guest_id];
              const r = roomsById[b.room_id];
              return (
                <div key={b.id} className="p-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-medium">
                      {g ? `${g.first_name} ${g.last_name}` : "—"}
                    </div>
                    <div className="text-xs text-stone-500">
                      {r?.name || "—"} · {b.check_in} → {b.check_out}
                    </div>
                  </div>
                  <button onClick={() => setQrFor(b.id)} data-testid={`qr-btn-${b.id}`}
                    className="inline-flex items-center gap-2 bg-blue-700 text-stone-50 px-4 py-1.5 text-sm rounded-sm hover:bg-blue-700">
                    <QrCode className="w-4 h-4" /> Genera QR
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {qrFor && <BookingQRDialog bookingId={qrFor} onClose={() => setQrFor(null)} />}
    </div>
  );
}
